<script lang="ts">
  import Button from '../common/Button.svelte';
  import FormInput from '../common/FormInput.svelte';
  import Badge from '../common/Badge.svelte';
  import Edit from '../icons/Edit.svelte';
  import { formatCurrency } from '../../lib/analytics.js';

  type Props = {
    actualIncome: number;
    incomeTarget: number;
    detectedIncome: number;
    isAutoDetected: boolean;
    density?: 'compact' | 'comfortable' | 'spacious';
    onSaveTarget: (target: number) => Promise<void>;
    onClearTarget: () => Promise<void>;
  };

  let {
    actualIncome,
    incomeTarget,
    detectedIncome,
    isAutoDetected,
    density = 'comfortable',
    onSaveTarget,
    onClearTarget
  }: Props = $props();

  let isEditing = $state(false);
  let editValue = $state('');
  let isSubmitting = $state(false);

  function startEditing() {
    editValue = incomeTarget.toString();
    isEditing = true;
  }

  function cancelEditing() {
    isEditing = false;
    editValue = '';
  }

  async function saveTarget() {
    const newTarget = parseFloat(editValue);
    if (isNaN(newTarget) || newTarget <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    isSubmitting = true;
    try {
      await onSaveTarget(newTarget);
      isEditing = false;
      editValue = '';
    } catch (err) {
      console.error('Error saving income target:', err);
      alert('Failed to save income target');
    } finally {
      isSubmitting = false;
    }
  }

  async function clearTarget() {
    isSubmitting = true;
    try {
      await onClearTarget();
      isEditing = false;
      editValue = '';
    } catch (err) {
      console.error('Error clearing income target:', err);
      alert('Failed to clear income target');
    } finally {
      isSubmitting = false;
    }
  }

  // Calculate percentage of income earned
  const percentage = $derived(incomeTarget > 0 ? (actualIncome / incomeTarget) * 100 : 0);
  const displayPercentage = $derived(Math.min(percentage, 100));
</script>

<div class="income-budget-card" class:compact={density === 'compact'} class:spacious={density === 'spacious'}>
  {#if !isEditing}
    <div class="income-actions">
      <button
        class="icon-button"
        onclick={startEditing}
        disabled={isSubmitting}
        aria-label="Edit income target"
        title="Edit"
      >
        <Edit size={16} />
      </button>
    </div>
  {/if}

  <div class="income-header">
    <div class="income-title-row">
      <h3 class="income-name">Income</h3>
    </div>
    <div class="income-info-container">
      <div class="income-badges">
        <Badge variant="success" size="xs">
          {percentage >= 100 ? 'On Track' : 'In Progress'}
        </Badge>
        {#if isAutoDetected}
          <Badge variant="info" size="xs">
            Auto-detected
          </Badge>
        {/if}
      </div>
    </div>
  </div>

  {#if isEditing}
    <div class="income-edit">
      <div class="edit-input-wrapper">
        <FormInput
          bind:value={editValue}
          type="number"
          placeholder={detectedIncome.toString()}
          disabled={isSubmitting}
        />
      </div>
      <div class="edit-actions">
        <Button variant="primary" size="sm" onclick={saveTarget} disabled={isSubmitting}>
          Save
        </Button>
        {#if !isAutoDetected}
          <Button variant="ghost" size="sm" onclick={clearTarget} disabled={isSubmitting}>
            Use Auto-Detected
          </Button>
        {/if}
        <Button variant="ghost" size="sm" onclick={cancelEditing} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </div>
  {:else}
    <div class="income-progress">
      <div class="progress-label">
        <span class="earned">{formatCurrency(actualIncome)}</span>
        <span class="separator">/</span>
        <span class="target">{formatCurrency(incomeTarget)}</span>
        <span class="percentage">({percentage.toFixed(1)}%)</span>
      </div>

      <div class="progress-bar">
        <div
          class="progress-fill"
          style:width="{displayPercentage}%"
        ></div>
      </div>
    </div>
  {/if}
</div>

<style>
  .income-budget-card {
    position: relative;
    background: var(--color-background);
    border: 1px solid var(--color-success-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    transition: all var(--transition-base);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-xl);
  }

  /* Density Variants */
  .income-budget-card.compact {
    padding: var(--spacing-sm);
    border-radius: var(--radius-md);
  }

  .income-budget-card.spacious {
    padding: var(--spacing-2xl);
    border-radius: var(--radius-xl);
  }

  .income-header {
    flex: 0 0 300px;
    margin-bottom: 0;
  }

  .income-progress {
    flex: 1;
    margin-bottom: 0;
  }

  .income-budget-card:hover {
    box-shadow: var(--shadow-md);
  }

  .income-budget-card .income-header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding-right: 80px; /* Space for icon button */
  }

  .compact .income-header {
    gap: var(--spacing-xs);
  }

  .spacious .income-header {
    gap: var(--spacing-md);
  }

  .income-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .income-info-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .compact .income-info-container {
    gap: var(--spacing-xs);
  }

  .spacious .income-info-container {
    gap: var(--spacing-md);
  }

  .income-badges {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
  }

  .compact .income-badges {
    gap: var(--spacing-xs);
  }

  .income-name {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-success-dark);
    margin: 0;
  }

  .compact .income-name {
    font-size: var(--font-size-base);
  }

  .spacious .income-name {
    font-size: var(--font-size-xl);
  }

  .income-actions {
    position: absolute;
    top: var(--spacing-md);
    right: var(--spacing-md);
    display: flex;
    gap: var(--spacing-xs);
    z-index: 10;
  }

  .compact .income-actions {
    top: var(--spacing-xs);
    right: var(--spacing-xs);
  }

  .spacious .income-actions {
    top: var(--spacing-lg);
    right: var(--spacing-lg);
  }

  .icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .icon-button:hover {
    background: var(--color-background-secondary);
    color: var(--color-text-primary);
  }

  .icon-button:active {
    transform: scale(0.95);
  }

  .icon-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .income-edit {
    display: flex;
    flex-direction: row;
    align-items: end;
    gap: var(--spacing-md);
  }

  .edit-input-wrapper {
    flex: 1;
    min-width: 0;
  }

  .edit-actions {
    display: flex;
    gap: var(--spacing-sm);
    flex-shrink: 0;
  }

  .income-budget-card .income-progress {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    flex: 1;
    margin-bottom: 0;
  }

  .progress-label {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-success-dark);
  }

  .earned {
    font-weight: var(--font-weight-semibold);
    color: var(--color-success-dark);
  }

  .separator {
    color: var(--color-success);
  }

  .target {
    color: var(--color-success-dark);
  }

  .percentage {
    color: var(--color-success);
    font-size: var(--font-size-xs);
    margin-left: var(--spacing-xs);
  }

  .progress-bar {
    width: 100%;
    height: 12px;
    background: var(--color-background);
    border-radius: var(--radius-full);
    overflow: hidden;
    position: relative;
    border: 1px solid var(--color-success-border);
  }

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--color-success-dark) 0%, var(--color-success) 100%);
    border-radius: var(--radius-full);
    transition: width var(--transition-base);
  }

  @media (max-width: 768px) {
    .income-budget-card {
      flex-direction: column;
      align-items: stretch;
    }

    .income-header {
      flex: auto;
      padding-right: 80px;
    }

    .income-progress {
      flex: auto;
    }

    .income-budget-card .income-header {
      margin-bottom: var(--spacing-lg);
    }

    .compact .income-header {
      margin-bottom: var(--spacing-sm);
    }

    .spacious .income-header {
      margin-bottom: var(--spacing-2xl);
    }

    .income-edit {
      flex-direction: column;
      align-items: stretch;
    }

    .edit-actions {
      width: 100%;
    }

    .edit-actions :global(button) {
      flex: 1;
    }
  }
</style>
